import { useState, useMemo, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ArrowUpRight, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { BRAND, SERVICES, SESSION_TYPES, IMAGES } from "@/lib/constants";

export default function Appointment() {
  const [searchParams] = useSearchParams();
  const preselected = searchParams.get("hizmet") || "";

  const [form, setForm] = useState({
    service: preselected,
    name: "",
    phone: "",
    email: "",
    sessionType: "",
    message: "",
  });

  useEffect(() => {
    if (preselected && !form.service) {
      setForm((f) => ({ ...f, service: preselected }));
    }
  }, [preselected]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedService = useMemo(
    () => SERVICES.find((s) => s.slug === form.service),
    [form.service]
  );

  const update = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const updateE = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!form.service || !form.name || !form.phone || !form.sessionType) {
      toast.error("Lütfen zorunlu alanları doldurunuz.", {
        description: "Hizmet, ad-soyad, telefon ve seans türü gereklidir.",
      });
      return;
    }

    const serviceName =
      SERVICES.find((s) => s.slug === form.service)?.title || form.service;
    const sessionName =
      SESSION_TYPES.find((s) => s.value === form.sessionType)?.label ||
      form.sessionType;

    const text = `Merhaba ${BRAND.name},
Sizden randevu almak istiyorum.

• Hizmet: ${serviceName}
• Seans Türü: ${sessionName}
• Ad Soyad: ${form.name}
• Telefon: ${form.phone}
${form.email ? `• E-posta: ${form.email}\n` : ""}${
      form.message ? `\nMesajım:\n${form.message}` : ""
    }

Teşekkür ederim.`;

    const url = `https://wa.me/${BRAND.whatsappPhone}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
    toast.success("WhatsApp'a yönlendiriliyorsunuz...", {
      description: "Mesajınızı göndererek randevu sürecini başlatabilirsiniz.",
    });
  };

  return (
    <div data-testid="page-appointment" className="bg-cream-50">
      {/* Header */}
      <section className="relative overflow-hidden pt-8 md:pt-12 pb-14 md:pb-20">
        <div className="aura-bg opacity-50" />
        <div className="relative page-shell grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-9 min-w-0">
            <div className="eyebrow mb-6">Randevu</div>
            <h1 className="max-w-full md:max-w-none font-display text-[16vw] sm:text-[13vw] md:text-[8vw] lg:text-[7.5rem] font-light leading-[0.95] tracking-normal text-midnight">
              <em className="text-lavender-700">Niyetini</em> yaz,
              <br />
              <em className="text-gold-soft italic">gerisini</em>
              <br className="sm:hidden" /> birlikte.
            </h1>
            <p className="mt-8 w-full max-w-full md:max-w-xl text-lavender-700 leading-relaxed text-[15px]">
              Bilgilerini paylaş; aşağıdaki form WhatsApp üzerinden bana
              ulaşmanı sağlayacak. 24 saat içinde sana özel bir program
              öneriyoruz.
            </p>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="pb-16 md:pb-24" data-testid="appointment-form-section">
        <div className="page-shell grid grid-cols-12 gap-8 md:gap-12">
          {/* Form */}
          <form
            onSubmit={handleSubmit}
            data-testid="appointment-form"
            className="col-span-12 md:col-span-8 form-elegant space-y-8 md:space-y-10 bg-cream-50 p-5 sm:p-8 md:p-12 border border-cream-300"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-7 md:gap-y-8">
              <div className="md:col-span-2 space-y-3">
                <Label htmlFor="service">Hizmet Seçimi *</Label>
                <Select value={form.service} onValueChange={update("service")}>
                  <SelectTrigger
                    id="service"
                    data-testid="form-service-trigger"
                    className="w-full"
                  >
                    <SelectValue placeholder="Bir hizmet seçiniz" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => (
                      <SelectItem
                        key={s.slug}
                        value={s.slug}
                        data-testid={`form-service-${s.slug}`}
                      >
                        {s.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label htmlFor="name">Ad Soyad *</Label>
                <Input
                  id="name"
                  data-testid="form-name"
                  value={form.name}
                  onChange={updateE("name")}
                  placeholder="Adınız ve soyadınız"
                />
              </div>

              <div className="space-y-3">
                <Label htmlFor="phone">Telefon *</Label>
                <Input
                  id="phone"
                  data-testid="form-phone"
                  value={form.phone}
                  onChange={updateE("phone")}
                  placeholder="+90 ..."
                />
              </div>

              <div className="space-y-3">
                <Label htmlFor="email">E-posta</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="form-email"
                  value={form.email}
                  onChange={updateE("email")}
                  placeholder="ornek@mail.com"
                />
              </div>

              <div className="space-y-3">
                <Label htmlFor="sessionType">Seans Türü *</Label>
                <Select
                  value={form.sessionType}
                  onValueChange={update("sessionType")}
                >
                  <SelectTrigger
                    id="sessionType"
                    data-testid="form-session-trigger"
                  >
                    <SelectValue placeholder="Seans türünü seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {SESSION_TYPES.map((s) => (
                      <SelectItem
                        key={s.value}
                        value={s.value}
                        data-testid={`form-session-${s.value}`}
                      >
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-2 space-y-3">
                <Label htmlFor="message">Niyetin & Mesajın</Label>
                <Textarea
                  id="message"
                  data-testid="form-message"
                  value={form.message}
                  onChange={updateE("message")}
                  placeholder="Hangi konuda destek almak istediğini kısaca paylaşabilirsin..."
                />
              </div>
            </div>

            <div className="pt-7 md:pt-8 border-t border-cream-300">
              <button
                type="submit"
                data-testid="form-submit-whatsapp"
                className="btn-whatsapp"
              >
                <MessageCircle size={20} />
                WhatsApp Üzerinden Randevuyu Tamamla
              </button>
              <p className="mt-5 text-center text-xs text-lavender-700/80 tracking-wide">
                Form WhatsApp uygulamasını açacak ve mesajını otomatik hazırlayacaktır.
              </p>
            </div>
          </form>

          {/* Side info */}
          <aside className="col-span-12 md:col-span-4 space-y-6 md:space-y-8">
            {selectedService ? (
              <div
                data-testid="selected-service-preview"
                className="p-6 sm:p-8 bg-midnight text-cream-50"
              >
                <div className="text-[10px] tracking-[0.35em] uppercase text-gold-soft mb-3">
                  Seçtiğin Hizmet
                </div>
                <h3 className="font-display text-3xl sm:text-4xl font-light leading-tight">
                  {selectedService.title}
                </h3>
                <p className="mt-4 text-cream-100/80 leading-relaxed text-[14px]">
                  {selectedService.description}
                </p>
                <div className="mt-6 pt-5 border-t border-cream-100/15 grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                  <div>
                    <div className="text-gold-soft tracking-[0.2em] uppercase text-[10px] mb-1">
                      Süre
                    </div>
                    <div className="font-display text-xl">
                      {selectedService.duration}
                    </div>
                  </div>
                  <div>
                    <div className="text-gold-soft tracking-[0.2em] uppercase text-[10px] mb-1">
                      Format
                    </div>
                    <div className="font-display text-base">
                      {selectedService.format}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-6 sm:p-8 border border-cream-300 bg-cream-50">
                <div className="img-zoom rounded-sm aspect-square mb-6">
                  <img src={IMAGES.crystal} alt="kristal" />
                </div>
                <h3 className="font-display text-3xl text-midnight leading-tight">
                  Sezgine güven.
                </h3>
                <p className="text-lavender-700 mt-4 text-[14px] leading-relaxed">
                  Hangi çalışmanın sana iyi geleceğinden emin değilsen,
                  formdaki mesaj alanına ihtiyacını yazman yeterli — birlikte
                  belirleriz.
                </p>
              </div>
            )}

              <div className="p-6 sm:p-8 border border-gold/30 bg-lavender-50/40">
              <h3 className="font-display text-2xl text-midnight">
                Süreç nasıl işler?
              </h3>
              <ol className="mt-5 space-y-4 text-[14px] text-lavender-700">
                {[
                  "Formu WhatsApp üzerinden gönder",
                  "24 saat içinde teyit ve uygunluk",
                  "Seansın için ödeme & takvim",
                  "Seans günü buluşma",
                ].map((s, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="number-marker">0{i + 1}</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ol>
            </div>
          </aside>
        </div>
      </section>

      {/* Other services strip */}
      <section className="py-14 md:py-20 bg-lavender-50/40" data-testid="appointment-services-strip">
        <div className="page-shell">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-5 mb-8 md:mb-10">
            <h2 className="font-display text-3xl md:text-4xl text-midnight">
              Farklı bir hizmet mi arıyorsun?
            </h2>
            <Link to="/hizmetler" data-testid="appt-all-services" className="link-gold text-xs tracking-[0.22em] uppercase">
              Tümünü Gör →
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-3">
            {SERVICES.map((s) => (
              <button
                key={s.slug}
                onClick={() => update("service")(s.slug)}
                data-testid={`quick-service-${s.slug}`}
                className={`p-5 text-left border transition-colors duration-300 ${
                  form.service === s.slug
                    ? "bg-midnight text-cream-50 border-midnight"
                    : "bg-cream-50 text-midnight border-cream-300 hover:border-gold"
                }`}
              >
                <div className="font-display text-lg leading-tight">{s.title}</div>
                <ArrowUpRight size={14} className="mt-2 opacity-60" />
              </button>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
